import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useFamilyStore } from '../../store/familyStore';
import { useEventStore } from '../../store/eventStore';
import { useTodoStore } from '../../store/todoStore';
import { useWeatherStore } from '../../store/weatherStore';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import { isToday } from '../../utils/calendar';
import { formatTime } from '../../utils/time';
import { Icon } from '../../utils/icons';
import { useToastStore } from '../../store/toastStore';

export default function MobileToday() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { family, fetchFamily, isLoading: isFamilyLoading } = useFamilyStore();
  const rawEvents = useEventStore((state) => state.events);
  const fetchEvents = useEventStore((state) => state.fetchEvents);
  const { todos, fetchTodos } = useTodoStore();
  const { weather, fetchWeather, isLoading: isWeatherLoading } = useWeatherStore();
  const { isDarkMode } = useThemeStore();
  const { showToast } = useToastStore();
  const tokens = getDesignTokens(isDarkMode);

  // Memoize today to prevent infinite loops - only recalculate once per day
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    return date;
  }, []); // Empty deps - only calculate once on mount

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Fetch data
  useEffect(() => {
    if (isAuthenticated) {
      fetchFamily().catch((error) => {
        console.error('Error fetching family:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load family', 'error');
      });
      
      // Fetch events for today (with some buffer for all-day events)
      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 1);
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 1);
      
      fetchEvents(
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      ).catch((error) => {
        console.error('Error fetching events:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load events', 'error');
      });
      
      fetchTodos().catch((error) => {
        console.error('Error fetching todos:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load todos', 'error');
      });
      fetchWeather('device').catch((error) => {
        console.error('Error fetching weather:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load weather', 'error');
      });
    }
  }, [isAuthenticated, fetchFamily, fetchEvents, fetchTodos, fetchWeather, today, showToast]);

  // Filter events for today
  const todayEvents = useMemo(() => {
    return rawEvents.filter((event) => isToday(event.startTime));
  }, [rawEvents]);

  // Filter and group events/todos by member
  const memberData = useMemo(() => {
    if (!family?.members) return [];

    const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const dayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    return family.members.map((member) => {
      // Filter events for this member
      const memberEvents = todayEvents
        .filter((event) => {
          // Check if member is in event.memberIds or event.members
          if (event.memberIds && event.memberIds.includes(member.userId)) {
            return true;
          }
          if (event.members && event.members.some((m) => m.memberId === member.userId)) {
            return true;
          }
          return false;
        })
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

      // Filter todos for this member
      const memberTodos = todos
        .filter((todo) => {
          // Only open todos
          if (todo.status === 'done') return false;
          
          // Must be assigned to this member
          if (todo.assigneeId !== member.userId) return false;
          
          // Must have due date
          if (!todo.dueDateTime) return false;
          
          // Parse due date
          let dueDate: Date;
          if (todo.dueDateTime instanceof Date) {
            dueDate = todo.dueDateTime;
          } else if (typeof todo.dueDateTime === 'string') {
            const dueStr = todo.dueDateTime;
            if (dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              const [year, month, day] = dueStr.split('-').map(Number);
              dueDate = new Date(year, month - 1, day);
            } else {
              const isoDate = new Date(dueStr);
              dueDate = new Date(isoDate.getUTCFullYear(), isoDate.getUTCMonth(), isoDate.getUTCDate());
            }
          } else {
            return false;
          }
          
          const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate(), 0, 0, 0);
          // Include today's todos and past due todos
          return dueDateStart.getTime() <= dayEnd.getTime();
        })
        .sort((a, b) => {
          // Sort by priority (critical > high > medium > low), then by due date
          const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
          const aPriority = priorityOrder[a.priority || 'medium'];
          const bPriority = priorityOrder[b.priority || 'medium'];
          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }
          if (a.dueDateTime && b.dueDateTime) {
            const aTime = a.dueDateTime instanceof Date ? a.dueDateTime : new Date(a.dueDateTime);
            const bTime = b.dueDateTime instanceof Date ? b.dueDateTime : new Date(b.dueDateTime);
            return aTime.getTime() - bTime.getTime();
          }
          return 0;
        });

      return {
        member,
        events: memberEvents,
        todos: memberTodos,
      };
    });
  }, [family?.members, todayEvents, todos, today]);

  // Get today's forecast for weather display
  const todayForecast = useMemo(() => {
    if (!weather?.forecast) return null;
    const todayStr = today.toISOString().split('T')[0];
    return weather.forecast.find((day) => day.date === todayStr);
  }, [weather, today]);

  if (!isAuthenticated) {
    return null;
  }

  if (isFamilyLoading && !family) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${tokens.border.accent}`}></div>
      </div>
    );
  }

  if (!family) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <div className={`${tokens.card.bg} ${tokens.card.border} border rounded-landing p-6 ${tokens.card.shadow}`}>
          <p className={tokens.text.primary}>No family found. Please create a family first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-y-auto scrollable">
        {/* Weather Banner */}
        <div className={`${tokens.card.bg} border-b ${tokens.border.light} px-4 py-3 flex items-center justify-between flex-shrink-0`}>
        {isWeatherLoading && !weather ? (
          <div className="flex items-center gap-2">
            <div className={`animate-spin rounded-full h-5 w-5 border-b-2 ${tokens.border.accent}`}></div>
            <span className={`text-sm ${tokens.text.muted}`}>Loading weather...</span>
          </div>
        ) : weather && todayForecast ? (
          <>
            <div className="flex items-center gap-3">
              <div className={`text-2xl font-bold ${tokens.text.primary}`}>
                {weather.current.temperature.fahrenheit}°F
              </div>
              <div>
                <div className={`text-sm font-semibold ${tokens.text.primary}`}>
                  {weather.current.condition}
                </div>
                <div className={`text-xs ${tokens.text.muted}`}>
                  {weather.location.name}, {weather.location.region}
                </div>
              </div>
            </div>
            <div className={`text-xs ${tokens.text.muted} flex gap-4`}>
              <div>H: <span className="font-semibold">{todayForecast.high.fahrenheit}°F</span></div>
              <div>L: <span className="font-semibold">{todayForecast.low.fahrenheit}°F</span></div>
            </div>
          </>
        ) : (
          <div className={`text-sm ${tokens.text.muted}`}>Weather unavailable</div>
        )}
      </div>

        {/* Person Cards */}
        <div className="px-3 py-2 space-y-3">
        {memberData.map(({ member, events, todos }) => (
          <div
            key={member.userId}
            className={`${tokens.card.bg} rounded-landing p-4 border ${tokens.border.light} flex-shrink-0`}
          >
            <div className="flex gap-4">
              {/* Member Sidebar */}
              <div className="flex-shrink-0 w-20 flex flex-col items-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-lg mb-2"
                  style={{ backgroundColor: member.color }}
                >
                  {member.name.charAt(0)}
                </div>
                <span className={`font-semibold text-sm text-center ${tokens.text.primary}`}>
                  {member.name}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Events */}
                <div>
                  <h4 className={`text-sm font-semibold ${tokens.text.secondary} mb-2`}>Events</h4>
                  {events.length > 0 ? (
                    <div className="space-y-2">
                      {events.map((event) => (
                        <div
                          key={event.id}
                          className={`rounded-sm p-2 text-xs border-l-2 touch-target min-h-[44px]`}
                          style={{
                            borderLeftColor: member.color,
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                          }}
                        >
                          <div className={`font-medium ${tokens.text.primary} mb-0.5`}>{event.title}</div>
                          <div className={`${tokens.text.muted} text-[10px]`}>
                            {event.isAllDay ? 'All Day' : formatTime(event.startTime)}
                          </div>
                          {event.location && (
                            <div className={`${tokens.text.muted} text-[10px] mt-0.5`}>{event.location}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-xs ${tokens.text.muted}`}>No events</div>
                  )}
                </div>

                {/* Todos */}
                <div>
                  <h4 className={`text-sm font-semibold ${tokens.text.secondary} mb-2`}>To Dos</h4>
                  {todos.length > 0 ? (
                    <div className="space-y-1.5">
                      {todos.map((todo) => (
                        <div
                          key={todo.todoId}
                          className={`rounded-sm p-1.5 text-xs flex items-start gap-1.5 touch-target min-h-[44px]`}
                          style={{
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                          }}
                        >
                          <input type="checkbox" className="mt-0.5" checked={todo.status === 'done'} readOnly />
                          <span className={`${tokens.text.secondary} flex-1`}>{todo.title}</span>
                          {todo.priority === 'high' && (
                            <span className="text-[10px] px-1 py-0.5 bg-orange-500 text-white rounded">!</span>
                          )}
                          {todo.priority === 'critical' && (
                            <span className="text-[10px] px-1 py-0.5 bg-red-500 text-white rounded">!!</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-xs ${tokens.text.muted}`}>No todos</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}
